import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReportIssueIntake from '../components/ReportIssueIntake';
import StatusBadge from '../components/StatusBadge';
import { DSCard, DSIcon } from '../design-system/primitives';
import './MachinePublic.css';

const IMPACT_LABELS = {
  game_down: 'Game Down',
  tournament_risk: 'Tournament Risk',
  observation: 'Observation'
};

// Maps this page's status colors onto the shared StatusBadge variants and
// keeps the colored status dot matching what the badge used to render itself.
const STATUS_BADGE_VARIANT = {
  green: 'success',
  orange: 'warning',
  red: 'error',
  blue: 'info'
};

const STATUS_DOT_COLOR = {
  green: 'var(--operational)',
  orange: 'var(--needs-attention)',
  red: 'var(--out-of-order)',
  blue: 'var(--tournament)'
};

function MachinePublic() {
  const { machineId } = useParams();
  const [searchParams] = useSearchParams();
  const [machine, setMachine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reporterName, setReporterName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [issueSubmitSuccess, setIssueSubmitSuccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showIssueForm, setShowIssueForm] = useState(searchParams.get('action') === 'report');
  const [upvotedIssueIds, setUpvotedIssueIds] = useState(new Set());

  const loadMachine = useCallback(async () => {
    try {
      setLoading(true);
      // Use axios directly for public endpoint (no auth token)
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      console.log('Loading machine from:', `${API_URL}/machines/${machineId}/public`);
      const response = await axios.get(`${API_URL}/machines/${machineId}/public`);
      console.log('Machine loaded:', response.data);
      setMachine(response.data);
    } catch (err) {
      console.error('Error loading machine:', err);
      setError('Failed to load machine information: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [machineId]);

  useEffect(() => {
    // Check if user is logged in
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setCurrentUser(user);
      // Auto-populate name for logged-in users (check both camelCase and snake_case)
      const firstName = user.firstName || user.first_name || '';
      const lastName = user.lastName || user.last_name || '';
      if (firstName || lastName) {
        setReporterName(`${firstName} ${lastName}`.trim());
      }
    }
    loadMachine();
  }, [machineId, loadMachine]);

  const handleSubmitIssue = async ({ description, gameUnplayable, affectsTournamentPlay }) => {
    try {
      setSubmitting(true);
      setError('');

      const API_URL = process.env.REACT_APP_API_URL || '/api';
      await axios.post(`${API_URL}/machines/${machineId}/report-issue`, {
        reporterName: reporterName.trim(),
        description,
        gameUnplayable,
        affectsTournamentPlay
      });

      setIssueSubmitSuccess(true);
    } catch (err) {
      setError('Failed to submit issue report. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportDone = () => {
    setIssueSubmitSuccess(false);
    if (!currentUser) {
      setReporterName('');
    }
    loadMachine();
    setShowIssueForm(false);
  };

  // Upvoting requires auth (server-side), so this is only wired up for
  // members who happen to be logged in while viewing the public QR page -
  // truly anonymous visitors just see upvote counts, not a clickable button.
  const handleUpvote = async (issueId) => {
    try {
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_API_URL || '/api';
      const response = await axios.post(
        `${API_URL}/machines/${machineId}/issues/${issueId}/upvote`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setUpvotedIssueIds((prev) => {
        const next = new Set(prev);
        if (response.data.userUpvoted) next.add(issueId);
        else next.delete(issueId);
        return next;
      });

      setMachine((prevMachine) => {
        if (!prevMachine) return prevMachine;
        const updatedOpenIssues = (prevMachine.open_issues || []).map((issue) =>
          issue.id === issueId ? { ...issue, upvote_count: response.data.upvoteCount } : issue
        );
        return { ...prevMachine, open_issues: updatedOpenIssues };
      });
    } catch (err) {
      console.error('Failed to upvote:', err);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      operational: { label: 'Operational', color: 'green' },
      needs_attention: { label: 'Needs Attention', color: 'orange' },
      maintenance: { label: 'Under Maintenance', color: 'orange' },
      out_of_order: { label: 'Out of Order', color: 'red' },
      in_tournament: { label: 'In Tournament', color: 'blue' }
    };
    return statusMap[status] || statusMap.operational;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="machine-public loading-container">
        <div className="loading-spinner"></div>
        <p>Loading machine information...</p>
      </div>
    );
  }

  if (error && !machine) {
    return (
      <div className="machine-public error-container">
        <div className="error-icon"><DSIcon name="xCircle" /></div>
        <h2>Machine Not Found</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!machine) {
    return (
      <div className="machine-public error-container">
        <div className="error-icon"><DSIcon name="xCircle" /></div>
        <h2>Machine Not Found</h2>
        <p>The machine you're looking for doesn't exist.</p>
      </div>
    );
  }

  const statusInfo = getStatusInfo(machine.status);
  // An Insider Connected machine sends its scores to the board itself, so there is
  // nothing to submit by hand. The same rule as the scorekeeping acceptsManualEntry flag.
  const showHighScoreButton = !machine.has_insider_connected;

  return (
    <div className="machine-public">
      <div className="machine-hero">
        {machine.image_url_large && (
          <div className="machine-hero-image-wrap">
            <img
              src={machine.image_url_large}
              alt={machine.name}
              className="machine-hero-image"
            />
          </div>
        )}

        <div className="machine-hero-body">
          <h1>{machine.name}</h1>

          <StatusBadge
            variant={STATUS_BADGE_VARIANT[statusInfo.color] || 'neutral'}
            uppercase
            className="status-badge"
          >
            <span className="status-dot" style={{ background: STATUS_DOT_COLOR[statusInfo.color] }} />
            <span className="status-text">{statusInfo.label}</span>
          </StatusBadge>

            {/* Action Buttons */}
            <div className="action-buttons">
              {showHighScoreButton && (
                <Link
                  to={`/scorekeeping/machines/${machineId}/submit`}
                  className="action-button action-button-primary"
                >
                  SUBMIT HIGH SCORE
                </Link>
              )}
              <button
                className="action-button action-button-secondary"
                onClick={() => setShowIssueForm(true)}
              >
                REPORT AN ISSUE
              </button>
              {machine.opdb_id && (
                <a
                  href={`https://app.matchplay.events/opdb/entries/${machine.opdb_id}/pintips`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-button action-button-link"
                >
                  PINTIPS
                </a>
              )}
            </div>
          </div>
      </div>

      {/* Issue Report Form */}
      {showIssueForm && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            // Close only on a click of the backdrop itself. Don't hand DSCard an
            // onClick just to stop propagation - that makes the card a keyboard
            // activation target and swallows Space/Enter inside the form.
            if (e.target === e.currentTarget) setShowIssueForm(false);
          }}
        >
          <DSCard cornerVariant="lg" className="modal-card issue-modal">
            <div className="form-header">
              {!issueSubmitSuccess && (
                <div>
                  <h2>Report an Issue</h2>
                </div>
              )}
              <button
                className="close-button"
                onClick={() => setShowIssueForm(false)}
              >
                <DSIcon name="x" />
              </button>
            </div>
            <ReportIssueIntake
              reporterName={reporterName}
              onReporterNameChange={setReporterName}
              reporterNameReadOnly={currentUser !== null}
              machineName={machine.name}
              knownIssues={machine.known_issues || []}
              openIssues={(machine.open_issues || []).map((issue) => ({ ...issue, user_has_upvoted: upvotedIssueIds.has(issue.id) }))}
              onUpvote={currentUser ? handleUpvote : undefined}
              submitting={submitting}
              error={error}
              success={issueSubmitSuccess}
              onSubmit={handleSubmitIssue}
              onDone={handleReportDone}
            />
          </DSCard>
        </div>
      )}

      {/* Known Issues - standing notes about this machine, separate from open tickets */}
      {machine.known_issues && machine.known_issues.length > 0 && (
        <div className="issues-section">
          <h2>Known Issues</h2>
          <div className="issues-list">
            {machine.known_issues.map((ki) => (
              <DSCard key={ki.id} cornerVariant="md" className="issue-card">
                <p className="issue-description">{ki.description}</p>
                {ki.guidance_text && (
                  <div className="issue-meta">{ki.guidance_text}</div>
                )}
              </DSCard>
            ))}
          </div>
        </div>
      )}

      {/* Open Tickets */}
      {machine.open_issues && machine.open_issues.length > 0 && (
        <div className="issues-section">
          <h2>Reported Issues</h2>
          <div className="issues-list">
            {machine.open_issues.map((issue) => (
              <DSCard key={issue.id} cornerVariant="md" className="issue-card">
                <div className="issue-card-top">
                  {issue.impact && (
                    <span className={`impact-pill impact-pill--${issue.impact}`}>
                      <span className="impact-pill-dot" />
                      {IMPACT_LABELS[issue.impact] || issue.impact}
                    </span>
                  )}
                  <div className="issue-content">
                    <p className="issue-description">{issue.description}</p>
                    {issue.assigned_to_name && (
                      <div className="assigned-badge">
                        Assigned to {issue.assigned_to_name}
                      </div>
                    )}
                    <div className="issue-meta">
                      Reported by: {issue.reporter_name} • {formatDate(issue.created_at)}
                    </div>
                    {typeof issue.upvote_count === 'number' && issue.upvote_count > 0 && (
                      <div className="upvote-count-pill">
                        {issue.upvote_count} other{issue.upvote_count !== 1 ? 's' : ''} reported this too
                      </div>
                    )}
                  </div>
                </div>
              </DSCard>
            ))}
          </div>
        </div>
      )}

      <div className="footer">
        <img src="/logo.png" alt="Capital Pinball" className="footer-logo" />
        <p>Capital Pinball Club</p>
      </div>
    </div>
  );
}

export default MachinePublic;
